import { forwardRef, useState } from 'react';
import { UnstyledButton, Group, Avatar, Text, rem, Menu, Flex } from '@mantine/core';
import { IconChevronRight, IconExternalLink } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import classes from './UserButton.module.css';
import { changeLanguage, getCurrentLanguage } from '../../utils/i18n';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { invalidateSession } from '../../services/sessionSecurity';
import Cookies from 'js-cookie';
import useCanUseCFDIInvoicing from '../../hooks/useCanUseCFDIInvoicing';
import { CompanyPlanLevel } from '../../enum/CompanyPlanLevelEnum';
import { useCustomerDisplay } from '../../customerDisplay/CustomerDisplayProvider';

interface UserButtonProps extends React.ComponentPropsWithoutRef<'button'> {}

const UserButton = forwardRef<HTMLButtonElement, UserButtonProps>(
  ({ ...others }: UserButtonProps, ref) => {
    const { name, email } = useSelector((state) => state.user?.user);
    return (
      <div className={classes.header}>
        <UnstyledButton
          ref={ref}
          style={{
            width: '100%',
            padding: 'var(--mantine-spacing-sm)',
            color: 'light-dark(var(--mantine-color-black), var(--mantine-color-dark-0))'
          }}
          {...others}>
          <Group
            display={'flex'}
            gap={5}
            style={{
              flexDirection: 'row',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              width: '100%'
            }}>
            <Avatar radius="xl" color="#003684">
              {name?.substring(0, 2).toUpperCase()}
            </Avatar>
            <Flex
              direction={'column'}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Text size="sm" fw={500} lineClamp={1}>
                {name}
              </Text>
              <Text c="dimmed" size="xs" lineClamp={1}>
                {email}
              </Text>
            </Flex>
            <IconChevronRight style={{ width: rem(14), height: rem(14) }} stroke={1.5} />
          </Group>
        </UnstyledButton>
      </div>
    );
  }
);

export function UserButtonMenu() {
  const display = useCustomerDisplay();
  const history = useHistory();

  const { t } = useTranslation(['translation', 'CommonButtons']);
  const { user, company } = useSelector((state) => state.user);
  const [currLang, setCurrLang] = useState(getCurrentLanguage());
  const { canUseCFDIInvoicing } = useCanUseCFDIInvoicing(user.storeId);
  const onLogout = () => {
    display.clear();
    invalidateSession(Cookies.get('token'), 'logout');
  };
  const menuItems = [];

  //SHOW SUSCRIPTION SESSION IN CASE IS SUSCRIBED TO A PLAN OR CFDI SERVICE
  if (
    user.isCompanyAdmin &&
    (company?.planLevel !== CompanyPlanLevel.TRIAL || canUseCFDIInvoicing)
  ) {
    menuItems.push(
      <Menu.Item
        key={'/settings/subscription' + '-userMenuButton'}
        component="a"
        onClick={() => {
          history.push('/settings/subscription');
        }}>
        {t('settings.sideMenu.subscription')}
      </Menu.Item>
    );
  }

  return (
    <Menu withArrow>
      <Menu.Target>
        <UserButton />
      </Menu.Target>
      <Menu.Dropdown>
        {menuItems}
        <Menu.Item
          component="a"
          onClick={() => {
            history.push('/profile');
          }}>
          {t('CommonButtons:profile')}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          onClick={() => {
            const nextLang = currLang.includes('es') ? 'en' : 'es';
            changeLanguage(nextLang);
            setCurrLang(nextLang);
          }}>
          {t('CommonButtons:changeLanguage')}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconExternalLink style={{ width: rem(14), height: rem(14) }} />}
          component="a"
          onClick={onLogout}
          color="red"
          target="_blank">
          {t('CommonButtons:logout')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
